import { supabase } from '../lib/supabase';
export class WidgetLayoutsService {
    static TABLE_NAME = 'widget_layouts';
    /**
     * Load current (default) layout from Supabase
     */
    static async loadCurrentLayout(userId) {
        try {
            console.log(`[WidgetLayouts] Loading current layout for user: ${userId}`);
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('widgets')
                .eq('user_id', userId)
                .eq('is_default', true)
                .single();
            if (error) {
                // PGRST116 means "no rows found" which is normal for first time
                if (error.code === 'PGRST116') {
                    console.log('[WidgetLayouts] No existing default layout found (expected on first load)');
                    return null;
                }
                console.error('[WidgetLayouts] Error loading current layout:', error);
                return null;
            }
            if (!data) {
                console.log('[WidgetLayouts] No data returned');
                return null;
            }
            console.log('[WidgetLayouts] Successfully loaded layout with', data.widgets?.length, 'widgets');
            return data.widgets || null;
        }
        catch (error) {
            console.error('[WidgetLayouts] Exception loading current layout:', error);
            return null;
        }
    }
    /**
     * Save current layout to Supabase
     */
    static async saveCurrentLayout(userId, widgets) {
        try {
            console.log(`[WidgetLayouts] Saving layout for user: ${userId}, widgets: ${widgets.length}`);
            // Check if default layout exists
            const { data: existingLayout, error: checkError } = await supabase
                .from(this.TABLE_NAME)
                .select('id')
                .eq('user_id', userId)
                .eq('is_default', true)
                .single();
            if (checkError && checkError.code !== 'PGRST116') {
                console.error('[WidgetLayouts] Error checking for existing layout:', checkError);
                return false;
            }
            if (existingLayout) {
                // Update existing default layout
                console.log('[WidgetLayouts] Updating existing default layout');
                const { error } = await supabase
                    .from(this.TABLE_NAME)
                    .update({
                    widgets,
                    updated_at: new Date().toISOString(),
                })
                    .eq('user_id', userId)
                    .eq('is_default', true);
                if (error) {
                    console.error('[WidgetLayouts] Failed to update widget layout:', error);
                    return false;
                }
                console.log('[WidgetLayouts] Successfully updated layout');
            }
            else {
                // Insert new default layout
                console.log('[WidgetLayouts] Creating new default layout');
                const { error } = await supabase
                    .from(this.TABLE_NAME)
                    .insert({
                    user_id: userId,
                    name: 'Current Layout',
                    is_default: true,
                    widgets,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                });
                if (error) {
                    console.error('[WidgetLayouts] Failed to insert widget layout:', error);
                    return false;
                }
                console.log('[WidgetLayouts] Successfully inserted new layout');
            }
            return true;
        }
        catch (error) {
            console.error('[WidgetLayouts] Exception saving widget layout:', error);
            return false;
        }
    }
    /**
     * Save a named layout
     */
    static async saveNamedLayout(userId, name, widgets) {
        try {
            const { error } = await supabase
                .from(this.TABLE_NAME)
                .insert({
                user_id: userId,
                name,
                is_default: false,
                widgets,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            });
            if (error) {
                console.error('Failed to save named layout:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to save named layout to Supabase:', error);
            return false;
        }
    }
    /**
     * Get all saved layouts for a user
     */
    static async getSavedLayouts(userId) {
        try {
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Failed to get saved layouts:', error);
                return [];
            }
            if (!data) {
                return [];
            }
            return data.map((row) => ({
                id: row.id,
                userId: row.user_id,
                name: row.name,
                isDefault: row.is_default,
                widgets: row.widgets,
                createdAt: new Date(row.created_at),
                updatedAt: new Date(row.updated_at),
            }));
        }
        catch (error) {
            console.error('Failed to get saved layouts from Supabase:', error);
            return [];
        }
    }
    /**
     * Load a specific saved layout
     */
    static async loadSavedLayout(userId, layoutId) {
        try {
            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select('widgets')
                .eq('user_id', userId)
                .eq('id', layoutId)
                .single();
            if (error) {
                console.error('Failed to load saved layout:', error);
                return null;
            }
            if (!data) {
                return null;
            }
            return data.widgets || null;
        }
        catch (error) {
            console.error('Failed to load saved layout from Supabase:', error);
            return null;
        }
    }
    /**
     * Delete a saved layout
     */
    static async deleteSavedLayout(userId, layoutId) {
        try {
            const { error } = await supabase
                .from(this.TABLE_NAME)
                .delete()
                .eq('user_id', userId)
                .eq('id', layoutId);
            if (error) {
                console.error('Failed to delete layout:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete layout from Supabase:', error);
            return false;
        }
    }
}
